import React from 'react';
import styles from './UsageGuide.module.css';

function UsageGuide() {
    return (
        <div className={styles.usageGuidePageContainer}>
            <h3>How to use</h3>
            <h5>What is Coffer ?</h5>
            <p>
                Coffer is a web app to handoff and manage assets from designers
                to mobile developers easy &amp; painless. Currently it supports
                Illustrations and Icons.
            </p>
            <h5>Who is Coffer meant for ?</h5>
            <p>
                Coffer is meant to be used by designers, developers and product
                managers.
            </p>
            <h5>What can you do with Coffer ?</h5>
            <p>You can do the following with Coffer:</p>
            <ol>
                <li>View the current set of assets</li>
                <li>Create a new asset (Admins only)</li>
                <li>Update assets (Admins only)</li>
            </ol>
            <h5>How are Illustrations structured in Coffer ?</h5>
            <p>
                There are 2 kinds of Illustrations : <code>Default</code> and{' '}
                <code>Regional</code>.
            </p>
            <p>
                <strong>Default Illustrations :</strong>
            </p>
            <ul>
                <li>
                    Default Illustrations are the default value of an
                    Illustration (like a fallback)
                </li>
                <li>Mandatory to provide while creating a new Illustration.</li>
                <li>
                    Default Illustrations do not have any region specific
                    references / colours and can work for all the regions.
                </li>
                <li>
                    Default Illustrations get bundled with the Aloha libraries
                    (both android and iOS)
                </li>
            </ul>
            <p>
                <strong>Regional Illustrations :</strong>{' '}
            </p>
            <ul>
                <li>
                    Regional Illustrations are the region specific value of an
                    Illustration.
                </li>
                <li>
                    Each regional illustration has a default illustration
                    associated with it.
                </li>
                <li>Optional to provide while creating a new Illustration.</li>
                <li>
                    You can provide a value for regional illustration for each
                    region
                </li>
                <li>
                    Regional Illustrations <strong>do not</strong> get bundled
                    with the Aloha libraries.
                </li>
                <li>
                    Regional Illustrations are served via Darkroom, and{' '}
                    <strong>
                        can therefore be updated without a Play Store/App Store
                        release.
                    </strong>
                </li>
            </ul>
            <h5>How to create a new default Illustration ?</h5>
            <p>
                <strong>Note:</strong> You need to be an <code>admin</code> in
                order to create new illustrations. Kindly reach out to us if you
                want to change your role to admin.
            </p>
            <ol>
                <li>
                    Click on <code>Add Default Illustration</code> button
                </li>
                <li>
                    Select the <code>Product Category</code> for the default
                    illustration
                </li>
                <li>
                    Select the <code>Illustration Category</code> for the
                    default illustration
                </li>
                <li>
                    Enter the <code>Illustration Name</code> for the default
                    illustration
                </li>
                <li>
                    Export the Illustration from sketch / abstract in svg format
                </li>
                <li>
                    Click on <code>Browse</code> button in Coffer and select the
                    Illustration svg you exported in the above step
                </li>
                <li>
                    Click on <code>Save</code> button to save your changes
                </li>
            </ol>
            <h5>How to update a default Illustration ?</h5>
            <p>
                <strong>Note:</strong> You need to be an <code>admin</code> in
                order to update default illustrations. Kindly reach out to us if
                you want to change your role to admin.
            </p>
            <ol>
                <li>
                    Search for the name of the illustration on the homepage and
                    click on it
                </li>
                <li>
                    Click on the <code>Edit</code> button in the Default
                    Illustration card
                </li>
                <li>
                    You cannot change the name of the Illustration once its
                    created since it might already be in use by products in code
                    and changing the name would break the code
                </li>
                <li>
                    Click on the <code>Replace</code> button to select the new
                    illustration svg
                </li>
                <li>
                    After selecting your new svg, click on <code>Save</code>{' '}
                    button to save your changes.
                </li>
            </ol>
            <h5>How to update a regional Illustration ?</h5>
            <p>
                <strong>Note:</strong> You need to be an <code>admin</code> in
                order to update regional illustrations. Kindly reach out to us
                if you want to change your role to admin.
            </p>
            <ol>
                <li>
                    Search for the name of the default illustration associated
                    with the regional illustration you wish to update and click
                    on it
                </li>
                <li>
                    Click on the <code>Edit</code> button for the regional
                    illustration card you want to replace
                </li>
                <li>
                    Click on the <code>Replace</code> button if you wish to
                    upload a new version for that specific region or click on
                    the <code>Reset to default</code> button if you wish to
                    reset the regional illustration to default value of that
                    illustration.
                </li>
                <li>
                    Click on <code>Save</code> button to save your changes.
                </li>
            </ol>
            <h5>How to create a new Icon ?</h5>
            <p>
                <strong>Note:</strong> You need to be an <code>admin</code> in
                order to create new icons. Kindly reach out to us if you want to
                change your role to admin.
            </p>
            <ol>
                <li>
                    Click on <code>Add Icon</code> button
                </li>
                <li>
                    Select the <code>Icon Category</code> for the icon
                </li>
                <li>
                    Select the <code>Icon Size</code> for the icon
                </li>
                <li>
                    Enter the <code>Icon Name</code> for the icon
                </li>
                <li>
                    Export the Icon from sketch / abstract in svg format. The
                    icon's size should be the same as the <code>Icon Size</code>
                    selected above
                </li>
                <li>
                    Click on <code>Browse</code> button in Coffer and select the
                    Icon svg you exported in the above step
                </li>
                <li>
                    Click on <code>Save</code> button to save your changes
                </li>
            </ol>
            <h5>How to update an Icon ?</h5>
            <p>
                <strong>Note:</strong> You need to be an <code>admin</code> in
                order to update icons. Kindly reach out to us if you want to
                change your role to admin.
            </p>
            <ol>
                <li>
                    Search for the name of the icon on the homepage and click on
                    it
                </li>
                <li>
                    You cannot change the name of the Icon once its created
                    since it might already be in use by products in code and
                    changing the name would break the code
                </li>
                <li>
                    Click on the <code>Replace</code> button to select the new
                    icon svg
                </li>
                <li>
                    After selecting your new svg, click on <code>Save</code>
                    button to save your changes.
                </li>
            </ol>
            <h5>When are the Assets available for use in the consumer app ?</h5>
            <p>
                <strong>Default Illustrations & Icons :</strong>
            </p>
            <ul>
                <li>
                    All the Default Illustrations and Icons which are
                    added/updated in Coffer are synced with the Aloha libraries
                    at the start of each week (on Monday evening).
                </li>
                <li>
                    The aloha libraries usually get released at the start of the
                    week (either tuesday or wednesday) containing the
                    Illustration and Icon updates from the previous week.
                </li>
            </ul>
            <p>
                <strong>Regional Illustrations :</strong>
            </p>
            <ul>
                <li>
                    The updates to Regional Illustrations can be seen
                    immediately on app restart since they are served via
                    Darkroom.
                </li>
            </ul>
            <h5>How to use Illustrations & Icons from Coffer in code ?</h5>
            <p>
                For details on how to use Illustrations in code refer the below
                guides:
            </p>
            <p>
                <strong>Android :</strong>{' '}
                <a href='https://go-jek.atlassian.net/wiki/spaces/DEVX/pages/1791297648/uxe+guide+android+Icons+and+Illustrations?atlOrigin=eyJpIjoiMjU4OWI5OTgzNjU1NDU5MDlhODJlYjc2ZTkyYTBkMWQiLCJwIjoiYyJ9'>
                    https://go-jek.atlassian.net/wiki/spaces/DEVX/pages/1791297648/uxe+guide+android+Icons+and+Illustrations
                </a>
            </p>
            <p>
                <strong>iOS :</strong>{' '}
                <a href='https://go-jek.atlassian.net/wiki/spaces/DEVX/pages/1748340048/uxe+guide+iOS+Icons+and+Illustrations?atlOrigin=eyJpIjoiZjAzNTMyZDVkMDczNGU4NDllZGQwNTAxYzhmMDhjNGMiLCJwIjoiYyJ9'>
                    https://go-jek.atlassian.net/wiki/spaces/DEVX/pages/1748340048/uxe+guide+iOS+Icons+and+Illustrations
                </a>
            </p>
            <h5>
                What to do if an Asset exists on Abstract but not on Coffer ?
            </h5>
            <p>
                In such a case please ask the designer on your team to get in
                touch with the Creatives team to upload the asset on Coffer.
            </p>
        </div>
    );
}

export default UsageGuide;
